import { Link } from 'react-router-dom';
import { DollarSign, Shield, CheckCircle, ArrowLeft, FileText, AlertTriangle, Award, Stethoscope } from 'lucide-react';

// Static, public-facing pricing & terms page. Linked from the landing
// page nav and footer. Mirrors STATED_TUITION_BY_PROGRAM (backend) and
// COURSES_WITH_PAID_EXAMS (backend) so what students see matches what
// the apprenticeship agreement actually charges.
//
// Tuition math (June 2026):
//   • Standard programs: $199 enrollment + $149 mid-term + $149 final = $497 collected upfront
//   • Doctorate programs: same $199 + $149 + $149 = $497 collected upfront
//   • Flat programs (GED, Cyber Security, Forklift OSHA): single $99, no exams
//   • Deferred Tuition = Stated Tuition − Total Collected
const PROGRAMS = [
  {
    code: 'cna-prep',
    title: 'Certified Medical Assistant (CMA) Training Program',
    delivery: 'Hybrid · Online RTI + Clinical Affiliation Form',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
    clinicalHours: '75 supervised clinical hours',
  },
  {
    code: 'lpn-prep',
    title: 'Practical Nurse Tech Apprenticeship Program',
    delivery: 'Hybrid · Online RTI + Clinical Affiliation Form',
    total: '$8,500',
    collected: '$497',
    deferred: '$8,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
    clinicalHours: '600 supervised clinical hours',
    accent: true,
  },
  {
    code: 'phlebotomy-prep',
    title: 'Phlebotomy Technician',
    delivery: 'Online · Self-paced',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'emt-prep',
    title: 'Emergency Medical Technician (EMT)',
    delivery: 'Hybrid · Online RTI + Clinical Affiliation Form',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
    clinicalHours: '150 supervised clinical hours',
  },
  {
    code: 'homeland-security-prep',
    title: 'Master Homeland Security Specialist',
    delivery: 'Online · FEMA ICS Aligned',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'medical-terminology-prep',
    title: 'Medical Terminology',
    delivery: 'Online · Self-paced',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'medical-aide-prep',
    title: 'Medical Aide — Outpatient Clinic',
    delivery: 'Online · 8-Module Curriculum',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'ged-prep',
    title: 'GED Adult Learning',
    delivery: 'Online · Self-paced',
    total: '$99',
    collected: '$99',
    deferred: 'None',
    breakdown: 'Single flat $99 — no exams, no deferred tuition',
    flat: true,
  },
  // Feb 2026 additions — Cyber Security, Forklift OSHA, Medical Billing & Coding
  {
    code: 'cybersecurity-prep',
    title: 'Cyber Security',
    delivery: 'Online · Self-paced',
    total: '$99',
    collected: '$99',
    deferred: 'None',
    breakdown: 'Single flat $99 — no exams, no deferred tuition',
    flat: true,
  },
  {
    code: 'forklift-osha-prep',
    title: 'Forklift OSHA Certification',
    delivery: 'Online · Self-paced',
    total: '$99',
    collected: '$99',
    deferred: 'None',
    breakdown: 'Single flat $99 — no exams, no deferred tuition',
    flat: true,
  },
  {
    code: 'medical-billing-coding-prep',
    title: 'Medical Billing & Coding',
    delivery: 'Online · Self-paced',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  // Doctorate programs — same $199 + $149 + $149 = $497 cycle as all other paid programs
  {
    code: 'doctor-natural-health',
    title: 'Doctor of Natural Health',
    delivery: 'Online · Self-paced · Doctorate',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'doctor-holistic-wellness',
    title: 'Doctor of Holistic Wellness',
    delivery: 'Online · Self-paced · Doctorate',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'herbal-health-practitioner',
    title: 'Herbal Health Practitioner Certification',
    delivery: 'Online · Self-paced · Doctorate',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'integrative-wellness-consultant',
    title: 'Integrative Wellness Consultant',
    delivery: 'Online · Self-paced · Doctorate',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
  {
    code: 'doctor-vital-health-sciences',
    title: 'Doctor of Vital Health Sciences — Practitioner',
    delivery: 'Online · Self-paced · Doctorate',
    total: '$2,500',
    collected: '$497',
    deferred: '$2,003',
    breakdown: '$199 enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  },
];

const PNTECH = {
  code: 'pn-tech',
  title: 'Practical Nurse (PN) Tech',
  delivery: 'Hybrid · Online RTI + Clinical Affiliation Form',
  total: '$2,500',
  collected: '$497',
  deferred: '$2,003',
  breakdown: '$199 program enrollment + $149 mid-term + $149 final = $497 collected · remainder deferred',
  clinicalHours: '300 supervised clinical hours',
};

const TERMS = [
  {
    title: '$199 Program Enrollment + Workforce-Development Apprenticeship',
    body: 'Effective June 2026, every paid program collects a single $199 program-enrollment fee at the time of sign-up, a $149 mid-term tuition fee, and a $149 final exam tuition fee — totaling $497 collected across the program. The remainder of the stated tuition is the Deferred Tuition value carried under your signed ETA-671 Apprenticeship Agreement. The training itself is delivered as online workforce-development apprenticeship instruction; the deferred tuition only becomes due upon material default. GED and Forklift OSHA remain single $99 flat-fee programs with no mid-term or final exam.',
  },
  {
    title: 'Stated Tuition & Deferred Tuition',
    body: 'Each program has a Stated Tuition that represents the full economic value of training, materials, instructor time, clinical-placement coordination, proctoring, credentialing, and certificate issuance. Students pay only the $497 collected total upfront; the Deferred Tuition (Stated Tuition − Collected) becomes due only upon material default of the Agreement.',
  },
  {
    title: 'Material Default',
    body: 'Material default means (a) withdrawal from the program without completing all modules, (b) misrepresentation on the application, or (c) breach of any covenant of the Agreement. Upon material default, the Deferred Tuition becomes immediately due along with reasonable collection costs and attorneys\' fees.',
  },
  {
    title: 'Authorization to Sell, Factor, or Assign',
    body: 'By signing the apprenticeship agreement, you expressly authorize MedCare to sell, assign, factor, or otherwise transfer the Tuition Agreement (and the resulting receivable) to one or more financing partners or factoring companies. Any assignee succeeds to all of MedCare\'s rights and may enforce them directly against you.',
  },
  {
    title: 'Hold-Harmless Clause',
    body: 'You agree to indemnify, defend, and hold harmless MedCare, its officers, directors, employees, instructors, clinical-placement partners, financing partners, assignees, and agents from any claims, damages, losses, liabilities, and expenses arising out of or related to your participation in the program, the factoring or assignment of the Agreement, or your breach of any term thereof.',
  },
  {
    title: 'Unconditional Promise to Pay & Survival',
    body: 'Your payment obligations are absolute, unconditional, and independent of any claim, defense, offset, or counterclaim. These obligations SURVIVE termination, withdrawal, dismissal, material default, any assignment or factoring of the Agreement, and the issuance or denial of any certificate of completion.',
  },
  {
    title: 'No-Refund Policy (Summary)',
    body: 'All sales are final. The $99 program-enrollment fee, $99 mid-term, and $99/$199 final-exam fees are non-refundable. For the full policy — including chargeback handling and cancellation steps — see the dedicated Refund Policy page.',
  },
  {
    title: 'Hybrid Clinical Programs',
    body: 'CMA Training, PN, PN Tech, and EMT are hybrid programs. Online RTI (Related Technical Instruction) is delivered through the portal, and the required supervised clinical hours must be completed at an external healthcare facility you select. MedCare provides a printable Clinical Affiliation Form to take to the site. Certificates of Completion are gated until the signed clinical form is uploaded and admin-verified.',
  },
  {
    title: 'Completion Guarantee',
    body: 'Upon successful completion of all program modules (and, where applicable, admin-verified clinical hours), MedCare will issue a USDOL-stamped Certificate of Completion (with appended transcript) within thirty (30) calendar days. If MedCare fails to deliver due to its sole fault, the program-enrollment fee paid will be refunded.',
  },
  {
    title: 'Retraining Guarantee',
    body: 'If you complete the program but do not pass an occupational placement assessment required to enter the workforce, MedCare will provide one (1) complimentary retake of the program — including all modules — within twelve (12) months of original completion.',
  },
  {
    title: 'USDOL Registered Apprenticeship',
    body: 'This program is a U.S. Department of Labor (USDOL) Registered Apprenticeship administered by MedCare Nursing Institute & Training Academy Inc. under the Office of Workforce Development. Graduates receive a USDOL-stamped certificate of completion along with an official transcript documenting Related Technical Instruction (RTI) hours and On-the-Job Training (OJT) hours.',
  },
];

const PricingPage = () => {
  return (
    <div className="landing-page" data-testid="pricing-page">
      {/* Government header */}
      <div className="gov-header" data-testid="gov-header">
        <span className="gov-header-pill gold">U.S. Department of Labor · Registered Apprenticeship</span>
      </div>

      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-logo">
          <img
            src={`${process.env.PUBLIC_URL || ''}/medcare_seal.png`}
            alt="MedCare Institutional Seal"
            data-testid="medcare-seal-logo"
            width="64"
            height="64"
            style={{ objectFit: 'contain', display: 'block', borderRadius: '50%' }}
          />
          <div className="nav-logo-text">
            <span className="nav-logo-title">MedCare Nursing Institute &amp; Training Academy</span>
            <span className="nav-logo-subtitle">Healthcare · Emergency Response · Technology Apprenticeships</span>
          </div>
        </div>
        <div className="nav-links">
          <Link to="/" className="nav-link" data-testid="back-home-link">
            <ArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Home
          </Link>
          <Link to="/register" className="btn btn-success btn-sm" data-testid="pricing-create-account-btn" style={{ background: '#22c55e' }}>
            Create Account
          </Link>
          <Link to="/login" className="btn btn-primary btn-sm" data-testid="pricing-login-btn">Student Portal</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero" style={{ paddingBottom: 32 }}>
        <div className="hero-badge">
          <DollarSign size={16} />
          $297 Collected · Remainder Deferred · Updated Feb 2026
        </div>
        <h1 data-testid="pricing-page-title">
          $297 Collected Upfront <span className="hero-accent">— Workforce-Development Apprenticeship</span>
        </h1>
        <p className="tagline" style={{ maxWidth: 880 }}>
          Standard programs collect <strong>$297 total</strong> across three milestones —
          <strong> $99 program enrollment</strong>, <strong>$99 mid-term exam</strong>,
          and <strong>$99 final exam</strong>. Doctorate programs collect
          <strong> $397 total</strong> ($99 + $99 + $199). The remainder of each program's
          <strong> Stated Tuition</strong> is carried as a <strong>Deferred Tuition</strong>
          balance under your signed ETA-671 Apprenticeship Agreement and is payable only
          on material default.
        </p>
      </section>

      {/* Pricing table */}
      <section className="programs-section" id="tuition-table" style={{ paddingTop: 0 }}>
        <h2 className="section-title">All Tuition Costs</h2>
        <p className="section-subtitle">
          Click <Link to="/register" data-testid="pricing-cta-register">Create Account</Link> to begin enrollment for any program.
        </p>

        <div className="pricing-program-grid" data-testid="pricing-program-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 20,
          marginTop: 16,
        }}>
          {[...PROGRAMS, PNTECH].map((p) => {
            const accent = p.accent ? '#0d9488' : (p.flat ? '#7c3aed' : '#0b2545');
            return (
              <div
                key={p.code}
                className="pricing-card"
                data-testid={`pricing-card-${p.code}`}
                style={{
                  background: '#ffffff',
                  borderRadius: 14,
                  padding: '22px 22px 18px',
                  border: `2px solid ${accent}`,
                  boxShadow: '0 6px 20px rgba(15,23,42,0.06)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Stethoscope size={20} color={accent} />
                  {p.accent && (
                    <span data-testid="pricing-card-lpn-flag" style={{
                      background: accent, color: '#fff', fontSize: '0.65rem', letterSpacing: 1,
                      padding: '3px 8px', borderRadius: 4, fontWeight: 700,
                    }}>HYBRID · 600 HRS</span>
                  )}
                  {p.flat && (
                    <span style={{
                      background: accent, color: '#fff', fontSize: '0.65rem', letterSpacing: 1,
                      padding: '3px 8px', borderRadius: 4, fontWeight: 700,
                    }}>FLAT</span>
                  )}
                </div>
                <h3 style={{ fontSize: '1.12rem', margin: '0 0 4px', color: '#0b2545' }}>{p.title}</h3>
                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0 0 14px' }}>{p.delivery}</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', borderLeft: `3px solid ${accent}` }}>
                    <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', fontWeight: 700 }}>Stated Tuition</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: accent }} data-testid={`pricing-total-${p.code}`}>{p.total}</div>
                  </div>
                  <div style={{ background: '#fef9c3', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #b45309' }}>
                    <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 1, color: '#7c2d12', fontWeight: 700 }}>Collected</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#b45309' }} data-testid={`pricing-collected-${p.code}`}>{p.collected}</div>
                  </div>
                  <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #64748b' }}>
                    <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', fontWeight: 700 }}>Deferred</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#334155' }} data-testid={`pricing-deferred-${p.code}`}>{p.deferred}</div>
                  </div>
                </div>

                <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.55 }}>
                  <div><strong>Collected breakdown:</strong> {p.breakdown}</div>
                  {p.clinicalHours && (
                    <div style={{ marginTop: 4 }}><strong>Clinical:</strong> {p.clinicalHours}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Terms & Conditions */}
      <section className="programs-section" id="terms" style={{ paddingTop: 0 }}>
        <div style={{
          background: '#0b2545',
          color: '#ffffff',
          borderRadius: 14,
          padding: '28px 30px',
          border: '2px solid #c9a227',
          marginTop: 12,
        }} data-testid="terms-and-conditions-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <FileText size={22} color="#f7d774" />
            <span style={{
              background: '#f7d774', color: '#0b2545', padding: '4px 12px',
              borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 1,
            }}>TERMS &amp; CONDITIONS</span>
          </div>
          <h3 style={{ color: 'white', margin: '4px 0 14px', fontSize: '1.55rem' }}>
            What you agree to when you enroll
          </h3>
          <p style={{ color: '#bfdbfe', fontSize: '0.92rem', lineHeight: 1.6, marginTop: 0 }}>
            The full terms below are also presented as the ETA-671 Apprenticeship &amp; Tuition Agreement
            during onboarding. You must e-sign that document before accessing any course content.
          </p>

          <div style={{ display: 'grid', gap: 14, marginTop: 18 }}>
            {TERMS.map((t, i) => (
              <div
                key={i}
                data-testid={`pricing-term-${i}`}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: 10,
                  padding: '14px 16px',
                  borderLeft: '3px solid #f7d774',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <CheckCircle size={14} color="#f7d774" />
                  <strong style={{ color: '#f7d774', fontSize: '0.92rem' }}>{t.title}</strong>
                </div>
                <p style={{ margin: 0, color: '#e2e8f0', fontSize: '0.85rem', lineHeight: 1.6 }}>
                  {t.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* No refund banner */}
        <div data-testid="pricing-no-refund-policy" style={{
          marginTop: 24, background: '#fef2f2', borderRadius: 12, padding: '20px 24px',
          border: '2px solid #fecaca', textAlign: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
            <AlertTriangle size={20} color="#dc2626" />
            <span style={{ fontWeight: 700, color: '#dc2626', fontSize: '1rem' }}>NO REFUND POLICY</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b', lineHeight: 1.55 }}>
            All program fees (enrollment, mid-term, final exam, first installment) are <strong>non-refundable</strong>.
            By enrolling, you acknowledge and agree to this policy.
          </p>
        </div>

        {/* USDOL Framework banner */}
        <div data-testid="pricing-apprenticeship-disclaimer" style={{
          marginTop: 16, background: '#ecfdf5', borderRadius: 12, padding: '18px 22px',
          border: '2px solid #6ee7b7', color: '#065f46',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={18} color="#065f46" />
            <span style={{ fontWeight: 700, fontSize: '0.93rem' }}>USDOL Registered Apprenticeship</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.86rem', lineHeight: 1.55 }}>
            Every program is delivered as a U.S. Department of Labor Registered Apprenticeship
            administered by MedCare Nursing Institute &amp; Training Academy Inc. under the
            Office of Workforce Development. Graduates receive a USDOL-stamped Certificate
            of Completion plus an official transcript.
          </p>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Link
            to="/register"
            className="btn btn-success btn-lg"
            data-testid="pricing-bottom-cta"
            style={{ background: '#22c55e', fontSize: '1.05rem', padding: '14px 32px' }}
          >
            <Award size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Create Account &amp; Begin Enrollment
          </Link>
        </div>
      </section>

      {/* Workforce-development disclaimer */}

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-logo">MedCare Nursing Institute &amp; Training Academy</div>
          <div className="footer-contact">
            <p>3150 E. Shelby Dr, Memphis TN 38118</p>
            <p>Phone: 901.625.1201</p>
            <p>Email: medcare.nursing@yahoo.com</p>
          </div>
          <div className="footer-badges">
            <span className="footer-badge">100% Online</span>
            <span className="footer-badge">U.S. Dept of Labor Registered</span>
            <span className="footer-badge">USDOL-Stamped Certificates</span>
          </div>
          <p className="footer-copyright" data-testid="footer-copyright">
            © 2026 MedCare Nursing Institute &amp; Training Academy. All rights reserved.
            <br />
            A program of the <strong>MedCare National Board of Nursing &amp; Vital Health Sciences Association</strong> —
            3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201 · medcare.nursing@yahoo.com
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
